import React, { FC, useState } from "react";
import { Badge, Grid, IconButton, List, ListItem, ListItemAvatar, ListItemText } from "@mui/material";
import ChatMessage, { MessageObj } from "./Message";
import { HandleFiles, handleFiles, isEmptyObject } from "../../../../Tools/Tools";
import IMemo from "../../Forms/Components/IMemo";
import clsx from "clsx";
import chatStyle from "./chat.module.css";
import ArticleIcon from "@mui/icons-material/Article";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import FolderIcon from "@mui/icons-material/Folder";
import DeleteIcon from "@mui/icons-material/Delete";
import { smetaModals } from "../../../../Tools/modalManager";
import { buildFileSelector } from "../../../../../TokenProcessing/TokenProcessing";
import { getLastLogin } from "../../../../../Url";
import { confirmLoadImg } from "../../../../../Forms/TextEditor";

interface ChatProps {
  chatData: {
    Chat: {
      items: [MessageObj];
    };
  };
  docId: string;
  handlePage: (data: any) => void;
}

const fileTypesForPreview = ["png", "jpeg", "jpg", "gif", "bmp", "webp"];
const ListWithDelete: FC = ({ fileList, onChange }) => {
  const [files, setFiles] = useState(fileList);

  const handleClickDelete = (key: string) => {
    const newFiles = { ...files };
    delete newFiles[key];
    setFiles(newFiles);
    onChange(newFiles);
  };

  return (
    <List>
      {Object.entries(files).map(([key, value], index) => {
        if (value.hidden) return null;
        return (
          <ListItem
            key={key + index}
            secondaryAction={
              <IconButton edge="end" aria-label="delete" onClick={() => handleClickDelete(key)}>
                <DeleteIcon />
              </IconButton>
            }
          >
            <ListItemAvatar>
              {fileTypesForPreview.includes(value?.fileType) ? (
                <img src={`data:image/${value?.fileType};base64,${value?.rcdata}`} style={{ width: 55, height: 25 }} />
              ) : (
                <ArticleIcon className={chatStyle.chatIconColor} />
              )}
            </ListItemAvatar>
            <ListItemText primary={key} />
          </ListItem>
        );
      })}
    </List>
  );
};

//eslint-disable-next-line
const Chat: FC<ChatProps> = ({ chatData, docId, handlePage }) => {
  const [messagesData, setMessagesData] = useState(chatData);
  const [showInfoBar, setShowInfoBar] = useState(false);
  const [files, setFiles] = useState<{} | HandleFiles>({});
  const [text, setText] = useState("");
  const [messageId, setMessageId] = useState("");
  const [imageData, setImageData] = useState({ dataType: "png", imageWidth: "", imageHeight: "", rcdata: "" });
  const [editText, setEditText] = useState("");

  const renderMessages = () => {
    if (isEmptyObject(messagesData?.Chat)) return null;
    return messagesData.Chat.items.map((value, index) => {
      return (
        <ChatMessage
          messageData={value}
          key={"message" + index}
          docId={docId}
          events={{ onDelete: handleDeleteClick, onEdit: handleEditClick }}
        />
      );
    });
  };

  /*
  .backgroundColorStimate.light {
  background-color: var(--bg-light);
}
  */

  const clearRedactorData = () => {
    setShowInfoBar(false);
    setFiles({});
    setText("");
    setImageData({ dataType: "png", imageWidth: "", imageHeight: "", rcdata: "" });
    setMessageId("");
    setEditText("");
  };

  const handleDeleteClick = ({ id }) => {
    const postData = {
      chat: {},
      drop: [id],
    };
    handlePage(postData);
  };

  const handleEditClick = ({ id, text: messageText, files: messageFiles, imageData: messageImageData }) => {
    if (id === messageId) return clearRedactorData();
    // setFiles(messageFiles || {});
    setShowInfoBar(true);
    setText(messageText || "");
    setImageData(messageImageData);
    setMessageId(id);
    setEditText(messageText || "");
    // setRedactorData({ id: "", text: messageText, imageData: messageImageData, files: messageFiles });
  };

  const handleSend = ({ messageText = text, messageFiles = files }) => {
    const lastLoginUser = getLastLogin().split(",")[1];
    const linksFromText = messageText.match(/(https):\/\/[^\s]*|(http):\/\/[^\s]*/gi);
    const parsedMessageText = linksFromText?.reduce((acc, val) => acc.replace(val, ""), messageText);
    const filesObj = Object.values({ ...linksFromText, ...messageFiles });
    const postDataFiles = filesObj.reduce((acc, value) => {
      const name = value?.name || value;
      return {
        ...acc,
        [name]: {
          rcdata: value?.rcdata,
        },
      };
    }, {});
    let item = {
      User: lastLoginUser,
      mine: "1",
      id: messageId,
      $content: parsedMessageText || messageText || "",
      Files: postDataFiles,
    };
    if (imageData.rcdata) item = { ...item, ...imageData };
    const postData = {
      Chat: [item],
    };

    handlePage(postData);
    setShowInfoBar(false);
    setFiles({});
    setText("");
  };

  const handleChange = (e) => {
    setText(e.target.value);
  };

  const showFileList = () => {
    if (messageId) return;
    const fileList = {
      ...files,
      "Вложенное фото": {
        rcdata: imageData.rcdata,
        fileType: imageData.dataType,
        hidden: messageId ? true : !imageData.rcdata,
      },
    };
    if (isEmptyObject(fileList) && !imageData.rcdata) return;
    let localFiles = fileList;
    smetaModals.open(
      <ListWithDelete
        fileList={fileList}
        onChange={(data: typeof files) => {
          // debugger;
          localFiles = { ...data };
        }}
      />,
      {
        Title: "Вложения сообщения",
        onSelect: () => {
          // debugger;
          const newImageData = localFiles["Вложенное фото"];
          setImageData({ ...imageData, dataType: newImageData?.fileType || "", rcdata: newImageData?.rcdata || "" });
          const newFiles = Object.entries(localFiles).reduce((acc, [key, value]) => {
            if (key === "Вложенное фото") return acc;
            return { ...acc, [key]: value };
          }, {});
          setFiles(newFiles);
        },
      },
    );
  };

  const setNewFiles = (fileList) => {
    setFiles({
      ...files,
      ...fileList,
    });
    setShowInfoBar(true);
  };

  const attachFiles = async () => {
    const fileList = await buildFileSelector();
    setNewFiles(fileList.files);
  };

  const handlePaste = async (e) => {
    const items = (e.clipboardData || window.clipboardData).items;
    const fileList = [];

    for (const item of items) {
      if (item.kind === "file") {
        const file = item.getAsFile();
        fileList.push(file);
      }
    }
    if (fileList.length === 0) return;
    e.preventDefault();
    e.stopPropagation();
    const filesData = await handleFiles(fileList);
    if (!filesData.filesNames) return;
    const [fileName] = filesData.filesNames;
    if (filesData.finalData[fileName].fileType.includes("data:image/")) {
      const { fileType, rcdata } = filesData.finalData[fileName];
      const dataType = fileType.match(/[a-z/]+(?=;)/);
      const needToLoad = await confirmLoadImg(true);
      setShowInfoBar(true);
      if (needToLoad) return setNewFiles(filesData.finalData);
      setImageData({ imageWidth: "", imageHeight: "", rcdata, dataType: dataType[0] });
    } else setNewFiles(filesData.finalData);
  };

  const onDrop = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    const filesData = await handleFiles(e.dataTransfer.files);
    setNewFiles(filesData.finalData);
  };

  return (
    <Grid
      container
      id={"chatContainer"}
      direction="column"
      onDragOver={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onDrop={onDrop}
      className={clsx(chatStyle.smetaChatWrapper)}
      sx={{
        justifyContent: "flex-start",
        alignItems: "stretch",
        flexWrap: "nowrap",
      }}
    >
      <Grid
        sx={{
          flex: "1",
          overflow: "auto",
        }}
      >
        {renderMessages()}
      </Grid>
      <Grid
        sx={{
          flex: "0 1",
          backgroundColor: "white",
        }}
      >
        <Grid sx={{ paddingLeft: 1, height: 36, width: "100%", display: showInfoBar ? "" : "none" }}>
          <Grid
            container
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Grid
              container
              direction="row"
              sx={{
                justifyContent: "flex-start",
                alignItems: "center",
              }}
              spacing={1}
            >
              <Grid sx={{ display: messageId ? "" : "none", ml: 1 }}>
                <EditIcon className={chatStyle.chatIconColor} />
              </Grid>

              <Grid>
                <Badge badgeContent={Object.keys(files).length} color="primary">
                  {imageData.rcdata ? (
                    <img
                      src={`data:image/${imageData.dataType};base64,${imageData.rcdata}`}
                      className={clsx(chatStyle.chatImagePreview)}
                      alt="Файлы"
                      onClick={showFileList}
                    />
                  ) : (
                    <IconButton onClick={showFileList}>
                      <FolderIcon className={chatStyle.chatIconColor} />
                    </IconButton>
                  )}
                </Badge>
              </Grid>
              <Grid sx={{ display: messageId ? "" : "none" }}>
                {`Редактирование`}
                <br />
                {editText}
              </Grid>
            </Grid>
            <Grid></Grid>
            <Grid>
              <IconButton onClick={clearRedactorData}>
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>

        <Grid
          container
          direction="row"
          sx={{
            justifyContent: "space-around",
            alignItems: "center",
          }}
        >
          <Grid>
            <IconButton onClick={attachFiles}>
              <AttachFileIcon />
            </IconButton>
          </Grid>
          <Grid sx={{ width: "89%" }}>
            <IMemo
              hideCharCount={true}
              placeholder={"Сообщение..."}
              Text={text}
              onChange={handleChange}
              onBlur={handleChange}
              onPaste={handlePaste}
              style={{
                resize: "none",
                fieldSizing: "content",
                outline: "none",
                maxHeight: "61px",
                width: "100%",
                minHeight: 36,
              }}
            />
          </Grid>
          <Grid>
            <IconButton onClick={handleSend}>
              <SendIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Chat;
